import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { getUserId } from 'ha/modules/AuthLogic/selectors';
import { useServices } from 'ha/services';
import { ErrorMessages } from 'ha/constants/ErrorMessages';
import { AlertType } from 'ha/modules/Alertbar/types';
import { useIntl } from 'ha/i18n';
import { useShowAlert } from 'ha/modules/Alertbar/helpers/ShowAlert';
import { Conversation } from '@ha/api/v2/types';
import { ConversationsFilters } from 'ha/constants/ConversationsFilters';
import { getChannelId } from 'ha/myredux/getChannelId';
import { getInboxCounts } from 'ha/modules/InboxCounter/selectors';
import { getInboxCounters } from 'ha/modules/InboxCounter/actions';
import {
  Client as TwilioClient,
  ConnectionState,
  Conversation as TwilioConversation,
  Message,
  State,
} from '@twilio/conversations';
import { MessageAttributes } from 'ha/modules/RealtimeMessaging/actions/receiveMessage';
import { NotificationType } from 'ha/modules/RealtimeMessaging/constants';
import { PAGE_SIZE } from '../constants';
import { InboxContext } from './InboxContext';

export const InboxProvider: React.FC = ({ children }) => {
  const { apiV2, twilio } = useServices();
  const { T } = useIntl();
  const dispatch = useDispatch();
  const userId = useSelector(getUserId)!;
  const channelId = useSelector(getChannelId)!;
  const showAlert = useShowAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = (searchParams.get('state') ?? undefined) as
    | ConversationsFilters
    | undefined;
  const page = searchParams.has('page') ? Number(searchParams.get('page')) : 1;

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const inboxCounters = useSelector(getInboxCounts);
  const [conversations, setConversations] = React.useState<Conversation[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(0);

  const fetchInboxCounters = React.useCallback(async () => {
    dispatch(getInboxCounters(userId));
  }, [dispatch, userId]);

  const fetchConversations = React.useCallback(
    (withLoading = false) => {
      if (withLoading) setIsLoading(true);
      apiV2
        .getConversations('me', {
          expand: 'messages,listing,advertiser,tenant,rentalConditions',
          limit: PAGE_SIZE,
          listingId: searchParams.get('listingId') ?? undefined,
          offset: page > 1 ? (page - 1) * PAGE_SIZE : 0,
          order: 'messages-desc',
          state:
            state === ConversationsFilters.ALL
              ? undefined
              : state || ConversationsFilters.INBOX,
          text: searchParams.get('text') ?? undefined,
        })
        .then(({ data }) => {
          if (withLoading) setIsLoading(false);
          setConversations(data.conversations);
          setTotalCount(data.count);
        })
        .catch(() => {
          console.error('Probably a 401');
        });
    },
    [apiV2, searchParams, page, state],
  );

  const archiveConversation = React.useCallback(
    (id: number, value: boolean) =>
      apiV2
        .toggleArchiveConversation(id, value)
        .then(() => {
          fetchInboxCounters();
          fetchConversations();
        })
        .catch(() => {
          showAlert({
            text: T(ErrorMessages.something_went_wrong),
            kind: AlertType.Danger,
          });
        }),
    [T, apiV2, fetchConversations, fetchInboxCounters, showAlert],
  );

  const starConversation = React.useCallback(
    (id: number, value: boolean) => {
      return apiV2
        .toggleFavoriteConversation(id, value)
        .then(() => {
          fetchInboxCounters();
          fetchConversations();
        })
        .catch(() => {
          showAlert({
            text: T(ErrorMessages.something_went_wrong),
            kind: AlertType.Danger,
          });
        });
    },
    [T, apiV2, fetchConversations, fetchInboxCounters, showAlert],
  );

  const setPageParam = React.useCallback(
    newPage => {
      setSearchParams(params => {
        params.set('page', newPage);
        return params;
      });
    },
    [setSearchParams],
  );

  const startSearch = React.useCallback(
    (searchQuery: { searchText?: string; searchListingId?: number }) => {
      const { searchText, searchListingId } = searchQuery;
      setSearchParams(params => {
        if (searchText) {
          params.set('text', searchText);
        } else {
          params.delete('text');
        }
        if (searchListingId) {
          params.set('listingId', String(searchListingId));
        } else {
          params.delete('listingId');
        }
        return params;
      });
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    let client: TwilioClient;
    let userConversation: TwilioConversation;

    async function handle() {
      const {
        data: { token: userRealtimeToken },
      } = await apiV2.getRealtimeToken('me');

      client = await twilio.init(userRealtimeToken);
      client.on('stateChanged', (twilioState: State) => {
        if (twilioState === 'initialized') {
          client.on(
            'connectionStateChanged',
            (connectionState: ConnectionState) => {
              if (connectionState === 'connected') {
                fetchConversations();
              }
            },
          );

          client.getConversationByUniqueName(channelId).then(conversation => {
            userConversation = conversation;
            conversation.on('messageAdded', (message: Message) => {
              const attributes =
                message.attributes as unknown as MessageAttributes;

              if (
                attributes?.type === NotificationType.conversationMessage ||
                attributes?.type === NotificationType.conversationRead ||
                attributes?.type === NotificationType.conversationNew
              ) {
                fetchConversations();
              }
            });
          });
        }
      });
      client.on('connectionError', () => {
        console.error('Twilio connection error');
      });
    }

    handle();

    return () => {
      if (client) client.removeAllListeners();
      if (userConversation) userConversation.removeAllListeners();
    };
  }, [apiV2, channelId, fetchConversations, fetchInboxCounters, twilio]);

  const contextValue = React.useMemo(
    () => ({
      inboxCounters,
      fetchConversations,
      conversations,
      archiveConversation,
      starConversation,
      totalCount,
      setPageParam,
      isLoading,
      startSearch,
    }),
    [
      inboxCounters,
      fetchConversations,
      conversations,
      archiveConversation,
      starConversation,
      totalCount,
      setPageParam,
      isLoading,
      startSearch,
    ],
  );

  return (
    <InboxContext.Provider value={contextValue}>
      {children}
    </InboxContext.Provider>
  );
};
